import { LoadingOutlined } from '@ant-design/icons';
import { L } from '@lib';
import { ProfileLogoInput } from '@services';
import { Utils } from '@utils';
import { message } from 'antd';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import Presentation from './MyLogo.presentation';

interface IProps {
  url?: string;
  disabled?: boolean;
  enableCrop?: boolean;
  handleLogoDelete?: () => Promise<void>;
  handleLogoUpdate?: (profileLogo: ProfileLogoInput) => Promise<any>;
}

export const MyLogo: React.FC<IProps> = observer(
  ({ url, disabled, enableCrop, handleLogoUpdate, handleLogoDelete }) => {
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [loading, setLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useEffect(() => {
      url && setFileList([{ url, name: 'logo', uid: '-1' }]);
    }, [url]);

    const handleUpload = (file: RcFile) => {
      setLoading(true);
      Utils.getBase64(file, async (base64) => {
        handleLogoUpdate &&
          (await handleLogoUpdate({
            name: file.name,
            contentType: file.type,
            content: base64,
          })
            .then((response) => {
              setFileList([{ url: response, name: 'logo', uid: '-1' }]);
              setLoading(false);
            })
            .catch(() => {
              setLoading(false);
            }));
      });
    };

    const handleRemove = () => {
      setLoading(true);
      setShowDeleteModal(false);
      handleLogoDelete &&
        handleLogoDelete().then(() => {
          setFileList([]);
          setLoading(false);
        });
    };

    const beforeCrop = useCallback((file: File) => {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error(`${L('The file format is invalid, please select an image!')}`);
      }
      const isLesserThen5MB = file.size / 1024 / 1024 < 5;
      if (!isLesserThen5MB) {
        message.error(`${L('File size exceeds the maximum limit of 5 MB!')}`);
      }
      return isJpgOrPng && isLesserThen5MB;
    }, []);

    const onPreview = useCallback(async (file: UploadFile) => {
      let src = file.url as string;
      if (!src) {
        src = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(file.originFileObj as RcFile);
          reader.onload = () => resolve(reader.result as string);
        });
      }
      const image = new Image();
      image.src = src;
      const imgWindow = window.open(src);
      imgWindow?.document.write(image.outerHTML);
    }, []);

    if (loading) return <LoadingOutlined />;

    return (
      <Presentation
        disabled={disabled}
        fileList={fileList}
        showDeleteModal={showDeleteModal}
        enableCrop={enableCrop}
        handleRemove={handleRemove}
        handleUpload={handleUpload}
        onPreview={onPreview}
        beforeCrop={beforeCrop}
        setShowDeleteModal={setShowDeleteModal}
      />
    );
  }
);
